import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { SequenceE } from "../ui/Sequence";
import CancelScanModal from "../ui/CancelScanModal";
import {
  cancelScan,
  useScanProgress,
  useConnectorAppHealth,
  getScanResult,
  getApiErrorInfo,
} from "../../api";
import { ErrorText } from "../ui/ErrorReference";
import { useSampleContext } from "../../contexts";
import { scanCheckNeedsReachback } from "../../util/scanCheck";
import ScanningProgress from "../ui/ScanningProgress";
import bulkScanImg from "../../static/img/Bulk_scan.png";
import traceScanImg from "../../static/img/Trace_scan.png";

// Reconnection strategy constants
const MAX_RECONNECT_ATTEMPTS = 6; // Per drop
const MAX_TOTAL_RECONNECT_ATTEMPTS = 12; // Per scan lifetime
const RECONNECT_DELAY_MS = 2000;

// Shared reusable scan wizard component
const ScanWizard = ({
  title,
  subtitle,
  step,
  pageID,
  changeStep,
  deviceId,
  getMeasurementHook,
  scanType,
  validateHook,
  initContextScan,
  reachback,
  infoModal,
}) => {
  // State variables
  const { scanHistory, setScanResults } = useSampleContext();

  // Check if connector is legacy (no health support)
  const connectorAppHealth = useConnectorAppHealth();
  const isLegacyConnector = connectorAppHealth.data?.healthSupported === false;
  const scanHistoryKey = useMemo(() => {
    if (scanType?.startsWith("bulk")) return "bulk";
    if (scanType?.startsWith("trace")) return "trace";
    return scanType;
  }, [scanType]);
  const [scanResults, setScanResultsLocal] = useState(
    scanHistory[scanHistoryKey] || scanHistory[scanType] || [],
  );
  const [scanState, setScanState] = useState(() => {
    const existingScans =
      scanHistory[scanHistoryKey] || scanHistory[scanType] || [];
    return existingScans.length > 0 ? "selectScans" : "start";
  });
  const [selected, setSelected] = useState({});
  const [counter, setCounter] = useState(1);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [unverifiedScan, setUnverifiedScan] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [scanProgress, setScanProgress] = useState({});
  const [scanComplete, setScanComplete] = useState(false);
  const [scanSteps, setScanSteps] = useState(null);
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);

  const awaitingScanRef = useRef(false);
  const awaitingValidateRef = useRef(false);
  const skipSyncRef = useRef(false); // prevents sync effect during history load
  const counterRef = useRef(counter); // Track current counter for callbacks
  const scanResultsRef = useRef(scanResults); // Track current scanResults for callbacks
  const scanIdRef = useRef(scanId);
  const legacyConnectorRef = useRef(isLegacyConnector);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0); // Attempts per drop
  const totalReconnectAttemptsRef = useRef(0); // Total attempts for this scan
  const reconnectingRef = useRef(false);
  const scheduleReconnectRef = useRef(null);

  // Load scan history from context when the page becomes active or scanType changes
  useEffect(() => {
    if (step !== pageID) return;
    skipSyncRef.current = true;
    const historicalScans =
      scanHistory[scanHistoryKey] || scanHistory[scanType] || [];
    setScanResultsLocal(historicalScans);
    setScanState(historicalScans.length > 0 ? "selectScans" : "start");
  }, [step, pageID, scanHistoryKey, scanType, scanHistory]);

  // Sync scanResults to context when scanResults changes
  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setScanResults(scanHistoryKey, scanResults);
  }, [scanResults, scanHistoryKey, setScanResults]);

  // If scans exist while on this page, ensure we show select state
  useEffect(() => {
    if (step === pageID && scanResults.length > 0 && scanState === "start") {
      setScanState("selectScans");
    }
  }, [step, pageID, scanResults, scanState]);

  // Keep counter aligned with existing scans
  useEffect(() => {
    if (step !== pageID) return;
    const maxId = scanResults.reduce((m, s) => Math.max(m, s.scanId || 0), 0);
    setCounter(maxId + 1);
  }, [step, pageID, scanResults]);

  // Keep refs in sync with state for callback access
  useEffect(() => {
    counterRef.current = counter;
  }, [counter]);

  useEffect(() => {
    scanResultsRef.current = scanResults;
  }, [scanResults]);

  useEffect(() => {
    scanIdRef.current = scanId;
  }, [scanId]);

  useEffect(() => {
    legacyConnectorRef.current = isLegacyConnector;
  }, [isLegacyConnector]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    reconnectingRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      clearReconnectTimer();
    };
  }, [clearReconnectTimer]);

  useEffect(() => {
    if (!scanId) {
      clearReconnectTimer();
      totalReconnectAttemptsRef.current = 0;
    }
  }, [scanId, clearReconnectTimer]);

  // Prevent scroll bar flickering by reserving space
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.scrollbarGutter = "stable";
    }
  }, []);

  // Format scanType for display
  const formatScanType = (type) => {
    if (type === "bulk-burst") return "Bulk";
    if (type === "trace-burst") return "Trace";
    return type;
  };

  // Image to help customers distinguish bulk vs trace scans
  const scanTypeImage = scanType?.startsWith("bulk")
    ? bulkScanImg
    : scanType?.startsWith("trace")
      ? traceScanImg
      : null;

  // Connector measurement hook - starts the scan and returns scan_id
  const {
    data: measurement,
    status: measurementStatus,
    isFetching: isFetchingMeasurement,
    refetch: refetchMeasurement,
  } = getMeasurementHook(deviceId, scanType, connectorAppErrorLogger);

  // Extract scan_id from measurement hook and set it for SSE monitoring
  // For legacy connectors: extract scan data directly from response
  // For modern connectors: extract scan_id for SSE tracking
  useEffect(() => {
    if (
      awaitingScanRef.current &&
      !isFetchingMeasurement &&
      step === pageID &&
      measurementStatus === "success"
    ) {
      // Legacy connector: scan data returned directly in measurement response
      if (isLegacyConnector) {
        const scanData = measurement?.data || measurement;

        // Check if scan was successful
        if (scanData?.success === false || scanData?.errorFlag === -1) {
          // Scan failed - return to appropriate state
          const hasExistingScans = (scanResults?.length || 0) > 0;
          setScanState(hasExistingScans ? "selectScans" : "start");
          setScanComplete(false);
          setError(true);
          setErrorMessage(
            scanData?.message || "Scan failed. Please try again.",
          );
          awaitingScanRef.current = false;
        } else {
          // Scan succeeded - proceed with validation
          setScanComplete(true);
          setUnverifiedScan({
            ...(scanData || measurement),
            scanId: counter,
          });
          awaitingValidateRef.current = true;
          awaitingScanRef.current = false;
        }
      } else {
        // Modern connector: extract scan_id for SSE tracking
        const id = measurement?.scan_id || measurement?.data?.scan_id;
        if (id) {
          setScanId(id);
        }
        // Extract scanSteps as well
        const steps = measurement?.scanSteps || measurement?.data?.scanSteps;
        if (steps) {
          setScanSteps(steps);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    measurement,
    isFetchingMeasurement,
    measurementStatus,
    step,
    pageID,
    isLegacyConnector,
    counter,
    scanResults,
  ]);

  // Memoize SSE callbacks to prevent EventSource recreation on every render
  const handleScanProgress = useCallback(
    (progress) => setScanProgress(progress),
    [],
  );
  const handleScanComplete = useCallback(
    (result) => {
      clearReconnectTimer();
      // Scan complete: measurement data now available from SSE result
      // Only process if we're still awaiting a scan
      if (awaitingScanRef.current) {
        // Check if scan succeeded or failed
        if (result?.success === false) {
          // Scan completed but failed - treat as error
          const hasExistingScans = (scanResultsRef.current?.length || 0) > 0;
          setScanState(hasExistingScans ? "selectScans" : "start");
          setScanComplete(false);
          setScanId(null); // Clear scanId to stop SSE listener
          setError(true);
          setErrorMessage(
            result?.error?.message || "Scan failed. Please try again.",
          );
          awaitingScanRef.current = false;
        } else {
          // Scan succeeded - proceed with validation
          setScanComplete(true);
          setUnverifiedScan({
            ...(result?.data || result),
            scanId: counterRef.current,
          });
          awaitingValidateRef.current = true;
          awaitingScanRef.current = false;
        }
      }
    },
    [clearReconnectTimer],
  );

  const finalizeScanError = useCallback((error) => {
    // SSE error or timeout
    const hasExistingScans = (scanResultsRef.current?.length || 0) > 0;
    setScanState(hasExistingScans ? "selectScans" : "start");
    setScanComplete(false);
    setScanId(null);
    setError(true);

    // Extract appropriate error message based on error type
    let errorMsg;
    if (error?.timeout) {
      errorMsg = "Scan timed out. Please try again.";
    } else if (error?.message) {
      errorMsg = error.message;
    } else {
      errorMsg = "Unexpected error during scan.";
    }

    setErrorMessage(errorMsg);
    awaitingScanRef.current = false;
  }, []);

  const attemptReconnect = useCallback(async () => {
    if (reconnectingRef.current) return;
    const currentScanId = scanIdRef.current;
    if (!currentScanId) {
      finalizeScanError({ message: "Scan connection lost." });
      return;
    }

    reconnectingRef.current = true;
    try {
      const response = await getScanResult(currentScanId);
      const status = response?.status;
      const payload = response?.data;

      if (status === 202 || payload?.status === "in_progress") {
        const progress =
          payload?.progress ||
          payload?.data?.progress ||
          payload?.progressInfo ||
          payload;
        setScanProgress(progress);
        reconnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        scheduleReconnectRef.current?.();
        return;
      }

      if (payload?.status === "timeout") {
        const timeoutError = new Error("Scan timed out");
        timeoutError.timeout = true;
        reconnectingRef.current = false;
        finalizeScanError(timeoutError);
        return;
      }

      if (payload?.status === "complete") {
        const completedResult = payload?.result || payload?.data?.result;
        reconnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        handleScanComplete(completedResult);
        return;
      }

      // Handle known error statuses
      if (
        payload?.status === "failed" ||
        payload?.status === "error" ||
        payload?.status === "cancelled"
      ) {
        const errorMsg =
          payload?.error?.message ||
          payload?.message ||
          `Scan ${payload?.status}.`;
        reconnectingRef.current = false;
        finalizeScanError({ message: errorMsg });
        return;
      }

      // Log warning for unknown status values
      if (payload?.status) {
        console.warn(
          `[ScanWizard] Unknown scan status from /scan-result: "${payload.status}". Treating as error.`,
        );
        reconnectingRef.current = false;
        finalizeScanError({
          message: `Unexpected scan status: ${payload.status}. Please try again.`,
        });
        return;
      }

      const result =
        payload?.result || payload?.data?.result || payload?.data || payload;
      reconnectingRef.current = false;
      reconnectAttemptsRef.current = 0;
      handleScanComplete(result);
    } catch (err) {
      reconnectingRef.current = false;
      if (err?.response?.status === 404) {
        finalizeScanError({ message: "Scan not found. Please try again." });
        return;
      }
      scheduleReconnectRef.current?.(err);
    }
  }, [finalizeScanError, handleScanComplete]);

  const scheduleReconnect = useCallback(
    (error) => {
      if (!awaitingScanRef.current) return;

      // Check per-drop limit
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        finalizeScanError(
          error || {
            message: "Unable to reconnect to the scan. Please try again.",
          },
        );
        return;
      }

      // Check per-scan lifetime limit
      if (totalReconnectAttemptsRef.current >= MAX_TOTAL_RECONNECT_ATTEMPTS) {
        finalizeScanError(
          error || {
            message:
              "Scan connection unstable. Please try again or contact support.",
          },
        );
        return;
      }

      reconnectAttemptsRef.current += 1;
      totalReconnectAttemptsRef.current += 1;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        attemptReconnect();
      }, RECONNECT_DELAY_MS);
    },
    [attemptReconnect, finalizeScanError],
  );

  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  const handleScanError = useCallback(
    (error) => {
      // If SSE drops, attempt reconnection to fetch scan result
      if (
        awaitingScanRef.current &&
        !legacyConnectorRef.current &&
        scanIdRef.current
      ) {
        scheduleReconnect(error);
        return;
      }

      finalizeScanError(error);
    },
    [finalizeScanError, scheduleReconnect],
  );

  // Monitor scan progress via SSE - measurement data comes from here
  // Only used for modern connectors; legacy connectors get data directly
  useScanProgress(
    isLegacyConnector ? null : scanId,
    handleScanProgress,
    handleScanComplete,
    handleScanError,
  );

  // Validation hook - only validate when scanComplete is true
  const {
    data: validateResponse,
    status: validateStatus,
    isFetching: isFetchingValidate,
    refetch: refetchValidate,
  } = validateHook(
    scanComplete ? unverifiedScan : null,
    sampleCheckErrorLogger,
  );

  // Two newest scans
  const getTwoNewestScans = (results) => {
    return results
      .slice()
      .sort((a, b) => b.scanId - a.scanId)
      .slice(0, 2);
  };
  const [twoNewestScans, setTwoNewestScans] = useState(() =>
    getTwoNewestScans(scanResults),
  );

  useEffect(() => {
    setTwoNewestScans(getTwoNewestScans(scanResults));
  }, [scanResults]);

  const doesScanExist = (scanId) =>
    twoNewestScans.some((scan) => scan.scanId === scanId);

  useEffect(() => {
    if (!doesScanExist(selected.scanId)) setSelected({});

    if (twoNewestScans.length !== 0) {
      setSelected(twoNewestScans[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoNewestScans]);

  function connectorAppErrorLogger(err) {
    // Detect connector app connection errors
    const isConnectorConnectionError =
      err?.code === "ECONNREFUSED" ||
      err?.code === "ENOTFOUND" ||
      err?.code === "ECONNABORTED" ||
      err?.message?.includes("Network Error") ||
      err?.message?.includes("ECONNREFUSED") ||
      err?.message?.includes("ERR_NETWORK");

    const hasExistingScans = (scanResults?.length || 0) > 0;
    setScanState(hasExistingScans ? "selectScans" : "start");
    setError(true);

    if (isConnectorConnectionError) {
      setErrorMessage(
        "Connector App unavailable. Please ensure the Connector App is running and try again.",
      );
    } else {
      setErrorMessage(
        getApiErrorInfo(
          err,
          "Failed to perform scan. Please ensure the device is connected and try again.",
        ).message,
      );
    }

    awaitingScanRef.current = false;
    // eslint-disable-next-line no-console
    console.error(err);
  }

  function sampleCheckErrorLogger(err) {
    const hasExistingScans = (scanResults?.length || 0) > 0;

    // Only go back to "start" if there are no other scans
    // If we have existing scans, stay in "selectScans" to show them
    if (!hasExistingScans) {
      setScanState("start");
    } else {
      setScanState("selectScans");
    }

    setError(true);
    setErrorMessage(
      getApiErrorInfo(err, "Failed to validate scan. Please try again."),
    );
    awaitingScanRef.current = false;
    awaitingValidateRef.current = false;
    // eslint-disable-next-line no-console
    console.error(err);
  }

  const handleCancel = async () => {
    try {
      setCancelling(true);
      // Disable EventSource first to stop SSE callbacks
      setScanId(null);
      clearReconnectTimer();
      // Clear refs and unverified scan to prevent callback processing
      awaitingScanRef.current = false;
      awaitingValidateRef.current = false;
      setUnverifiedScan(undefined);
      // Now call API to cancel the scan on the device
      await cancelScan(deviceId, scanId);
      // Handle success - clear remaining state
      setScanComplete(false);
      setScanProgress(null);
      setScanSteps(null);
      setScanState(twoNewestScans.length > 0 ? "selectScans" : "start");
      setError(false);
      setErrorMessage("");
      setShowCancelModal(false);
      setCancelling(false);
      skipSyncRef.current = true;
    } catch (err) {
      // Handle error
      setError(true);
      setCancelling(false);

      setErrorMessage(
        getApiErrorInfo(err, "Failed to cancel scan. Please try again.")
          .message,
      );
    }
  };

  const handleModal = () => setShowModal(true);

  const handleScan = () => {
    setScanState("scanning");
    setError(false);
    setErrorMessage("");
    setScanComplete(false);
    setScanProgress(null);
    setScanId(null);
    setScanSteps(null);
    clearReconnectTimer();
    awaitingScanRef.current = true;
    awaitingValidateRef.current = false;
    refetchMeasurement();
    setShowModal(false);
    skipSyncRef.current = true;
  };

  useEffect(() => {
    if (scanComplete && unverifiedScan && awaitingValidateRef.current) {
      setScanState("validating");
      refetchValidate();
    }
  }, [scanComplete, unverifiedScan, refetchValidate]);

  useEffect(() => {
    if (!isFetchingValidate && step === pageID) {
      if (validateStatus === "success" && awaitingValidateRef.current) {
        let verifiedScan = { ...unverifiedScan };
        const errorCheck = validateResponse?.errorCheck;
        verifiedScan.verified = errorCheck?.errorFlag === 0;
        verifiedScan.errorCheck = {
          errorFlag: errorCheck?.errorFlag,
          modelPrediction: errorCheck?.modelPrediction,
          reconstructionError: errorCheck?.reconstructionError_intensity,
          signalQuality: errorCheck?.signalQuality,
        };
        setScanResultsLocal((prev) => [...prev, verifiedScan]);
        setCounter((prev) => prev + 1);
        skipSyncRef.current = false;
        setError(false);
        awaitingValidateRef.current = false;
        setScanState("selectScans");
      } else if (validateStatus === "error" && awaitingValidateRef.current) {
        // Validation failed - just reset state, don't add failed scan to results
        awaitingValidateRef.current = false;
        setError(true);
        setErrorMessage("Scan validation failed. Please try again.");
        setScanState("selectScans");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validateResponse,
    isFetchingValidate,
    validateStatus,
    step,
    pageID,
    unverifiedScan,
  ]);

  // Close cancel modal when not scanning
  useEffect(() => {
    if (scanState !== "scanning" && showCancelModal) {
      setShowCancelModal(false);
    }
  }, [scanState, showCancelModal]);

  // When navigating away from this page while a scan is in progress,
  // cancel the ongoing scan to avoid background activity and open connections.
  useEffect(() => {
    if (step !== pageID && scanState === "scanning") {
      // Best-effort cancellation; log errors for debugging but do not re-throw to avoid breaking navigation.
      Promise.resolve(cancelScan(deviceId, scanId)).catch((error) => {
        // Log the error to aid debugging; ignore it otherwise to preserve navigation behavior.
        /* eslint-disable no-console */
        console.error("Failed to cancel scan during navigation:", error);
        /* eslint-enable no-console */
      });
    }
  }, [step, pageID, scanState, deviceId]);
  if (step !== pageID) return null;

  const handleSelectSequence = (sequence) => {
    selected && selected.scanId === sequence.scanId
      ? setSelected({})
      : setSelected(sequence);
  };

  const { reasonKey } = reachback;
  // Analyte and Advisory scans continue plainly; anything else goes to reachback
  const selectedNeedsReachback = scanCheckNeedsReachback(selected.errorCheck);

  return (
    <div className='shadow card d-flex flex-column flex-grow-1'>
      <div className='card-body full-height justify-content-between'>
        {/* Header - always visible */}
        <div className='d-flex justify-content-between flex-wrap mb-3'>
          <h5 className='card-title w-100 fs-1 d-flex align-items-center justify-content-center mb-0'>
            <span>{title}</span>
            <button
              className='btn ms-1 p-1'
              aria-label='Info'
              onClick={handleModal}
            >
              <i
                className='bi bi-info-circle-fill'
                style={{ color: "#1ca3f0" }}
              ></i>
            </button>
          </h5>
          

          {/* Conduct Scan button - only visible in "selectScans" state in header */}
          {scanState === "selectScans" && (
            <div className='d-flex w-100 justify-content-center align-items-center order-2 mt-3'>
              <button
                className='btn btn-success'
                type='button'
                variant='outline-success'
                onClick={handleScan}
              >
                {`Conduct ${formatScanType(scanType)} Scan`}
              </button>
            </div>
          )}

          {infoModal &&
            infoModal({ showModal, handleClose: () => setShowModal(false) })}
          <CancelScanModal
            show={showCancelModal}
            handleClose={() => setShowCancelModal(false)}
            handleConfirm={() => {
              handleCancel();
            }}
            loading={cancelling}
          />
        </div>

        {/* Error message - visible in all states */}
        {error && errorMessage && (
          <div className='col-12 mb-3'>
            <div
              className='alert alert-danger alert-dismissible fade show'
              role='alert'
            >
              <div className='d-flex align-items-center'>
                <i className='bi bi-exclamation-triangle-fill me-2'></i>
                <div>
                  <strong>Error:</strong> <ErrorText error={errorMessage} />
                </div>
              </div>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={() => {
                  setError(false);
                  setErrorMessage("");
                  setShowTroubleshoot(false);
                }}
              ></button>
              <div className='mt-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-outline-dark'
                  onClick={() => setShowTroubleshoot((p) => !p)}
                  aria-expanded={showTroubleshoot}
                  aria-controls='scan-troubleshooting'
                >
                  {showTroubleshoot
                    ? "Hide Troubleshooting"
                    : "Show Troubleshooting"}
                </button>
              </div>
              {showTroubleshoot && (
                <div id='scan-troubleshooting' className='mt-3 small'>
                  <ul className='mb-0'>
                    <li>
                      Confirm the Connector App is running and up to date.
                    </li>
                    <li>Verify the device is firmly connected and powered.</li>
                    <li>Ensure the laser is armed and ready for scanning.</li>
                    {scanType === "trace-burst" && (
                      <li>
                        Verify the chip is properly inserted in the reader.
                      </li>
                    )}
                    {scanType === "bulk-burst" && (
                      <li>
                        Verify the sample is properly inserted in the reader.
                      </li>
                    )}

                    <li>Check that you are connected to the internet.</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STATE: START - Reference image and button in middle of card */}
        {scanState === "start" && (
          <div className='d-flex flex-column align-items-center justify-content-evenly flex-grow-1'>
            <button
              className='btn btn-success btn-lg w-25 mb-3'
              type='button'
              onClick={handleScan}
            >
              {`Conduct ${formatScanType(scanType)} Scan`}
            </button>
            {scanTypeImage && (
              <img
                src={scanTypeImage}
                alt={`${formatScanType(scanType)} scan example`}
                style={{
                  maxWidth: "min(40%, 500px)",
                  width: "100%",
                  height: "auto",
                  borderRadius: "0.5rem",
                }}
              />
            )}
          </div>
        )}

        {/* STATE: SCANNING - Progress status and cancel button */}
        {scanState === "scanning" && (
          <div className='d-flex align-items-center justify-content-center flex-grow-1'>
            <ScanningProgress
              stepNumber={scanProgress?.step_num}
              scanSteps={scanSteps}
              onCancel={
                isLegacyConnector ? null : () => setShowCancelModal(true)
              }
            />
          </div>
        )}

        {/* STATE: VALIDATING - Show spinner while validating scan */}
        {scanState === "validating" && (
          <div className='d-flex align-items-center justify-content-center flex-grow-1 h2 '>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Validating scan...</span>
            </div>
            <div className='ms-3'>Validating...</div>
          </div>
        )}

        {/* STATE: SELECT SCANS - Display scan results */}
        {scanState === "selectScans" && twoNewestScans.length !== 0 && (
          <form className='row justify-content-evenly d-flex align-items-stretch flex-wrap flex-grow-1'>
            {twoNewestScans.map((seq) => (
              <div
                className='col-12 col-xl-6 mt-2 spectrum-card-container'
                key={seq.scanId}
              >
                <SequenceE
                  sequence={seq}
                  isSelected={selected.scanId === seq.scanId}
                  valid={seq.verified}
                  onSelect={handleSelectSequence}
                />
              </div>
            ))}
          </form>
        )}

        {/* Navigation buttons - hidden while scanning or validating */}
        {scanState !== "scanning" && scanState !== "validating" && (
          <div className='row mt-4 align-content-center'>
            <span className='col-6 d-flex justify-content-center'>
              <button
                type='button'
                className='btn btn-secondary mx-auto w-75'
                onClick={() => {
                  changeStep("back");
                }}
                disabled={scanState === "scanning"}
              >
                Back
              </button>
            </span>

            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip id='tooltip-disabled'>
                  {scanState === "start"
                    ? "Click Conduct Scan to take a scan, or skip this step"
                    : Object.keys(selected).length === 0 &&
                        twoNewestScans.length === 0
                      ? "If you proceed without conducting a scan, no scan data will be saved or analyzed for this step"
                      : Object.keys(selected).length === 0 &&
                          twoNewestScans.length > 0
                        ? "If you proceed without selecting a scan, your previous scans will not be saved or analyzed"
                        : selectedNeedsReachback
                          ? "This scan will be submitted for Reachback Service"
                          : "Continue to the next step"}
                </Tooltip>
              }
              container={document.body}
              popperConfig={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                ],
              }}
            >
              <span className='col-6 d-flex justify-content-center'>
                {scanState === "start" || Object.keys(selected).length === 0 ? (
                  <button
                    className='btn btn-warning mx-auto w-75'
                    type='button'
                    onClick={() => {
                      initContextScan(null);
                      setScanState("start");
                      changeStep("forward");
                      reachback.removeReachbackReason(reasonKey);
                    }}
                    disabled={scanState === "scanning"}
                  >
                    Skip {formatScanType(scanType)} Scan
                  </button>
                ) : (
                  <button
                    type='button'
                    className='btn btn-primary mx-auto w-75'
                    onClick={() => {
                      if (selectedNeedsReachback) {
                        reachback.addReachbackReason({
                          [reasonKey]: selected.errorCheck?.signalQuality,
                        });
                      } else {
                        reachback.removeReachbackReason(reasonKey);
                      }
                      initContextScan(selected);
                      setScanState("start");
                      changeStep("forward");
                    }}
                    disabled={
                      Object.keys(selected).length === 0 ||
                      scanState === "scanning"
                    }
                  >
                    {!selectedNeedsReachback ||
                    Object.keys(selected).length === 0
                      ? "Continue"
                      : "Continue and Submit for Reachback Service"}
                  </button>
                )}
              </span>
            </OverlayTrigger>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanWizard;
